import React, { useMemo } from 'react';
import { MultiMetoroMetricsChart } from "../../Charts/MultiMetoroMetricsCharts";
import { ChartType } from "../../Charts/MetoroChart";
import { parseTimeSeriesResponse } from '../timeSeriesUtils';
import { MarkdownRenderer } from "../MarkdownRenderer";
import {DefaultToolCallRenderer} from "../ToolRenderers";

// Message interface for the TimeSeriesToolRenderer
interface Message {
    role: 'user' | 'assistant' | 'system' | 'tool';
    content: string;
    tool_name?: string;
    tool_call?: boolean;
    tool_call_id?: string;
    tool_call_function_name?: string;
    tool_call_function_args?: string;
    tool_call_response_id?: string;
    tool_call_response_content?: string;
    screenshot?: string;
}

// ToolRendererProps interface
export interface ToolRendererProps {
    message: Message;
    isToolCall: boolean;
}

// Define the component type with shouldRender
interface TimeSeriesToolRendererType extends React.FC<ToolRendererProps> {
    shouldRender: (message: Message) => boolean;
}

// Base component without memo
const BaseTimeSeriesToolRenderer: TimeSeriesToolRendererType = function TimeSeriesToolRenderer({ message, isToolCall }: ToolRendererProps) {
    // Don't render the response for a time series tool call
    if (message.tool_call_response_id) {
        return null;
    }

    // We want to render the call only
    if (!message.tool_call_function_args) {
        return null;
    }

    const chartData = useMemo(() => {
        if (!message.tool_call_function_args) return null;
        return parseTimeSeriesResponse(message.tool_call_function_args);
    }, [message.tool_call_function_args]);

    if (!chartData) {
        return null;
    }

    return (
        <div>
            <DefaultToolCallRenderer message={message}/>
            <div className="w-full h-[400px] mt-4">
                <MultiMetoroMetricsChart
                    startTime={chartData.startTime}
                    endTime={chartData.endTime}
                    metricSpecifiers={chartData.metricSpecifiers}
                    type={ChartType.Line}
                    title={chartData.title}
                    formulas={chartData.formulas}
                    styling={{
                        borderless: false
                    }}
                    className='w-full h-[400px]'
                />
            </div>
        </div>
    );
};

// Add the static method
BaseTimeSeriesToolRenderer.shouldRender = (message: Message): boolean => {
    // Only render the tool call, not the response
    if (message.tool_call_response_id !== undefined) {
        return false;
    }
    return true;
};

// Create the memoized component
const MemoizedTimeSeriesToolRenderer = React.memo(BaseTimeSeriesToolRenderer);

// Export the memoized version with the static method
export const TimeSeriesToolRenderer = Object.assign(MemoizedTimeSeriesToolRenderer, {
    shouldRender: BaseTimeSeriesToolRenderer.shouldRender
}) as TimeSeriesToolRendererType; 